import React from 'react'
import styles from './EventsEmbed.scss'
import PropTypes from 'prop-types'
import { connect, Provider } from 'react-redux'
import { initStore } from './redux/initStore'
import { Calendar } from 'common/Calendar'
import { Map } from 'immutable'
import moment from 'moment'
import { firstVisibleDay, lastVisibleDay, indexEventsByDate } from 'lib/calendarUtils'
import * as eventsActions from 'lib/api/events'
import { ClubEventRow } from './ClubEventRow'
import logoUrl from 'assets/images/horizontal-logo-white.svg'
import { EventsList } from './EventsList'

const CALENDAR_DISPLAY = 'calendar'
const LIST_DISPLAY = 'list'

const sharedComponentDidUpdate = (props, prevProps) => {
  if (prevProps.tagname !== props.tagname) {
    return true
  }

  if (prevProps.listDays !== props.listDays) {
    return true
  }
}

export class CalendarEvents extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      eventsByDate: Map(),
      date: moment()
    }
  }

  componentDidMount () {
    this.loadMonthData()
  }

  loadMonthData = () => {
    const { clubId, loadAllEvents, tagname } = this.props
    const { date } = this.state

    const start = firstVisibleDay(date)
    const end = lastVisibleDay(date)

    const minDateStr = moment(start).subtract(1, 'days').format('YYYY-M-D')
    const maxDateStr = moment(end).add(1, 'days').format('YYYY-M-D')

    let params = {
      organization_id: clubId,
      starts_on_min: minDateStr,
      starts_on_max: maxDateStr
    }

    if (tagname) {
      params = Object.assign(params, { tagname: tagname })
    }

    clubId && loadAllEvents(params, this.addEventsToState)
  }

  componentDidUpdate = (prevProps) => {
    if (sharedComponentDidUpdate(this.props, prevProps)) {
      this.setState({ eventsByDate: Map() }, () => {
        this.loadMonthData()
      })
    }
  }

  addEventsToState = ({ value: { results } }) => {
    const { eventsByDate } = this.state

    const indexed = indexEventsByDate(results, startAccessor, endAccessor)
    const withNewData = eventsByDate.mergeDeep(indexed)

    this.setState({
      eventsByDate: withNewData
    })
  }

  onDateChange = (date) => {
    this.setState({ date }, this.loadMonthData)
  }

  render () {
    const { clubId, title, description, color, showDateTime, showLocation, showOrganizers, showImage } = this.props
    const { date, eventsByDate } = this.state

    const wrapperProps = {
      showDateTime,
      showLocation,
      showOrganizers,
      showImage
    }

    const _ClubEventRow = (rowProps) => <ClubEventRow wrapperProps={wrapperProps} {...rowProps} />

    return (
      <div className={styles.EventsEmbed}>
        <EmbedHeader
          title={title}
          description={description}
          clubId={clubId}
          color={color} />
        <Calendar
          date={date}
          onChange={this.onDateChange}
          events={eventsByDate}
          hideTotals
          color={color}
          RowComponent={_ClubEventRow}
          startAccessor={startAccessor} />
      </div>
    )
  }
}

const getListRangeStart = () => {
  return moment().subtract(1, 'days')
}

const getListRangeEnd = (listDays) => {
  const days = parseInt(listDays, 10)
  return moment().add(days, 'days')
}

class ListEvents extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      eventsLoaded: false
    }
  }

  componentDidMount () {
    this.loadListData()
  }

  loadListData = () => {
    const { clubId, loadAllEvents, listDays, tagname } = this.props

    const minDateStr = getListRangeStart().format('YYYY-M-D')
    const maxDateStr = getListRangeEnd(listDays).format('YYYY-M-D')

    let params = {
      organization_id: clubId,
      starts_on_min: minDateStr,
      starts_on_max: maxDateStr,
      clearPrevious: true
    }

    if (tagname) {
      params = Object.assign(params, { tagname: tagname })
    }

    if (clubId) {
      this.setState({ eventsLoaded: false })
      loadAllEvents(params).then(() => this.setState({ eventsLoaded: true }))
    }
  }

  componentDidUpdate (prevProps) {
    if (sharedComponentDidUpdate(this.props, prevProps)) {
      this.loadListData()
    }
  }

  render () {
    const { color, clubId, title, description, events, listDays, tagname, ...props } = this.props
    const { eventsLoaded } = this.state

    const rangeStart = getListRangeStart()
    const rangeEnd = getListRangeEnd(listDays)
    const eventsInRange = events.valueSeq().filter((e) => {
      return rangeStart.isSameOrBefore(e.startsOn) && rangeEnd.isSameOrAfter(e.endsAt || e.endsOn || e.startsAt || e.startsOn)
    })
    const eventsWithTag = tagname ? eventsInRange.filter((event) => event.tagNames && event.tagNames.indexOf(tagname) >= 0) : eventsInRange
    const sortedEvents = eventsWithTag.sortBy((e) => e.startsAt || e.startsOn)

    return (
      <div className={styles.EventsEmbed}>
        <EmbedHeader
          title={title}
          description={description}
          clubId={clubId}
          color={color} />
        <EventsList
          events={sortedEvents.toArray()}
          loaded={eventsLoaded}
          color={color}
          {...props} />
      </div>
    )
  }
}

class EmbedHeader extends React.PureComponent {
  render () {
    const { color, clubId, title, description } = this.props

    return (
      <div className={styles.header}>
        <div className={styles.left}>
          <div className={styles.title} style={{ color: `#${color}` }}>
            <a href={`/clubs/${clubId}/home`} style={{ color: `#${color}` }} target='_blank'>{title}</a>
          </div>
          <div className={styles.description}>
            {description}
          </div>
        </div>
        <div className={styles.logo}>
          <a href={`/clubs/${clubId}/home`} target='_blank'><img src={logoUrl} /></a>
        </div>
      </div>
    )
  }
}

const startAccessor = (event) => {
  return event.startsOn
}

const endAccessor = (event) => {
  return event.endsOn || startAccessor(event)
}

class _EventsEmbed extends React.PureComponent {
  render () {
    const { display, ...props } = this.props
    return display === LIST_DISPLAY
      ? <ListEvents {...props} />
      : <CalendarEvents {...props} />
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    events: state.eventsEmbed.get('events')
  }
}

const mapDispatchToProps = {
  loadAllEvents: eventsActions.loadAllEvents
}

const EventsEmbedContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(_EventsEmbed)

const store = initStore()

export class EventsEmbed extends React.PureComponent {
  static propTypes = {
    clubId: PropTypes.string.isRequired,
    color: PropTypes.string,
    tagname: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    display: PropTypes.string,
    listDays: PropTypes.string,
    showDateTime: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    showLocation: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    showOrganizers: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    showImage: PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
  }
  static defaultProps = {
    color: '6982FF',
    title: 'Upcoming Rides',
    display: CALENDAR_DISPLAY,
    listDays: '28',
    showDateTime: true,
    showLocation: true,
    showOrganizers: true,
    showImage: true
  }

  render () {
    const { showDateTime, showLocation, showOrganizers, showImage, ...props } = this.props

    const booleanProps = {
      showDateTime: valToBool(showDateTime),
      showLocation: valToBool(showLocation),
      showOrganizers: valToBool(showOrganizers),
      showImage: valToBool(showImage)
    }

    return (
      <Provider store={store}>
        <EventsEmbedContainer {...booleanProps} {...props} />
      </Provider>
    )
  }
}

// "false", while truthy should actually be false
const valToBool = (val) => (val === 'true' || val === true)
